import { Inject, Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { catchError, EMPTY, take, tap } from 'rxjs';
import { UserSettingsChanged } from '../store/auth.actions';
import { AuthState } from '../store/auth/auth.state';
import { UserService } from './user/user.service';

export interface NotificationVisitOptions {
  persistRemote?: boolean;
}

export interface INotificationVisitService {
  visitedNotification(notif: string): boolean;
  setNotificationVisit(notif: string, options?: NotificationVisitOptions): void;
}

@Injectable({
  providedIn: 'root',
})
export class NotificationVisitService implements INotificationVisitService {
  constructor(
    @Inject(Store) private readonly store: Store,
    @Inject(UserService) private readonly userService: UserService,
  ) { }

  visitedNotification(notif: string): boolean {
    try {
      return this.isVisitedInLocalStorage(notif) || this.isVisitedInUserSettings(notif);
    } catch (error) {
      return false;
    }
  }

  setNotificationVisit(notif: string, options: NotificationVisitOptions = {}): void {
    const { persistRemote = true } = options;
    if (this.isVisitedInLocalStorage(notif) || this.isVisitedInUserSettings(notif)) return;

    this.updateLocalStorage(notif, true);
    if (persistRemote) {
      this.updateUserSettings(notif);
    }
  }

  private isVisitedInLocalStorage(notif: string): boolean {
    return localStorage.getItem(this.localStorageKey(notif)) === 'true';
  }

  private isVisitedInUserSettings(notif: string): boolean {
    // Read per-user visited notifications from company.userSettings (not company-level) to avoid cross-user leakage
    const userSettings = this.store.selectSnapshot(AuthState.company)?.userSettings;
    return userSettings?.custom?.visitedNotification?.[notif] || false;
  }

  private updateLocalStorage(notif: string, value: boolean): void {
    localStorage.setItem(this.localStorageKey(notif), String(value));
  }

  private localStorageKey(notif: string): string {
    const userId = this.store.selectSnapshot(AuthState.user)?.id;
    return `visited-${userId}-${notif}`;
  }

  private updateUserSettings(notif: string): void {
    this.userService
      .editProperties('me', { [`custom.visitedNotification.${notif}`]: true })
      .pipe(
        take(1), // Auto-complete after first emission to prevent memory leaks
        tap(() => {
          this.store.dispatch(new UserSettingsChanged({
            custom: { visitedNotification: { [notif]: true } },
          }));
        }),
        catchError(error => {
          return EMPTY;
        }),
      )
      .subscribe();
  }
}
